/**
 * Based on: https://github.com/WICG/focus-visible
 * 
 * @deprecated Use CSS :focus-visble instead. 
 */
export class FocusVisible {
    private hadKeyboardEvent = true;
    private hadFocusVisibleRecently = false;
    private hadFocusVisibleRecentlyTimeout: number | null = null;
    private _onInitialPointerMove: (e: Event) => void;

    private inputTypesAllowlist: any = {
        text: true,
        search: true,
        url: true,
        tel: true,
        email: true,
        password: true,
        number: true,
        date: true,
        month: true,
        week: true,
        time: true,
        datetime: true,
        'datetime-local': true
    };

    public constructor(public scope: Document | DocumentFragment = document) {
        this._onInitialPointerMove = (e) => this.onInitialPointerMove(e);
        // For some kinds of state, we are interested in changes at the global scope
        // only. For example, global pointer input, global key presses and global
        // visibility change should affect the state at every scope:
        document.addEventListener('keydown', (e) => this.onKeyDown(e), true);
        document.addEventListener('mousedown', (e) => this.onPointerDown(e), true);
        document.addEventListener('pointerdown', (e) => this.onPointerDown(e), true);
        document.addEventListener('touchstart', (e) => this.onPointerDown(e), true);
        document.addEventListener('visibilitychange', (e) => this.onVisibilityChange(e), true);

        this.addInitialPointerMoveListeners();

        // For focus and blur, we specifically care about state changes in the local
        // scope. This is because focus / blur events that originate from within a
        // shadow root are not re-dispatched from the host element if it was already
        // the active element in its own scope:
        scope.addEventListener('focus', (e: any) => this.onFocus(e), true);
        scope.addEventListener('blur', (e: any) => this.onBlur(e), true);

        // We detect that a node is a ShadowRoot by ensuring that it is a
        // DocumentFragment and also has a host property. This check covers native
        // implementation and polyfill implementation transparently. If we only cared
        // about the native implementation, we could just check if the scope was
        // an instance of a ShadowRoot.
        if (scope.nodeType === Node.DOCUMENT_FRAGMENT_NODE && (scope as any).host) {
            // Since a ShadowRoot is a special kind of DocumentFragment, it does not
            // have a root element to add a class to. So, we add this attribute to the
            // host element instead:
            (scope as any).host.setAttribute('data-js-focus-visible', '');
        } else if (scope.nodeType === Node.DOCUMENT_NODE) {
            document.documentElement.classList.add('js-focus-visible');
            document.documentElement.setAttribute('data-js-focus-visible', '');
        }

        // It is important to wrap all references to global window and document in
        // these checks to support server-side rendering use cases
        // @see https://github.com/WICG/focus-visible/issues/199
        if (typeof window !== 'undefined' && typeof document !== 'undefined') {
            // Make the polyfill helper globally available. This can be used as a signal
            // to interested libraries that wish to coordinate with the polyfill for e.g.,
            // applying the polyfill to a shadow root:
            // window.applyFocusVisiblePolyfill = applyFocusVisiblePolyfill;

            // Notify interested libraries of the polyfill's presence, in case the
            // polyfill was loaded lazily:
            var event;

            try {
                event = new CustomEvent('focus-visible-polyfill-ready');
            } catch (error) {
                // IE11 does not support using CustomEvent as a constructor directly:
                event = document.createEvent('CustomEvent');
                event.initCustomEvent('focus-visible-polyfill-ready', false, false, {});
            }

            window.dispatchEvent(event);
        }

        if (typeof document !== 'undefined') {
            // Apply the polyfill to the global document, so that no JavaScript
            // coordination is required to use the polyfill in the top-level document:
            // this.applyFocusVisiblePolyfill(document);
        }
    }

    /**
   * Helper function for legacy browsers and iframes which sometimes focus
   * elements like document, body, and non-interactive SVG.
   * @param {Element} el
   */
    private isValidFocusTarget(el: any) {
        if (
            el &&
            el !== document &&
            el.nodeName !== 'HTML' &&
            el.nodeName !== 'BODY' &&
            'classList' in el &&
            'contains' in el.classList
        ) {
            return true;
        }
        return false;
    }

    /**
   * Computes whether the given element should automatically trigger the
   * `focus-visible` class being added, i.e. whether it should always match
   * `:focus-visible` when focused.
   * @param {Element} el
   * @return {boolean}
   */
    private focusTriggersKeyboardModality(el: any) {
        var type = el.type;
        var tagName = el.tagName;

        if (tagName === 'INPUT' && this.inputTypesAllowlist[type] && !el.readOnly) {
            return true;
        }

        if (tagName === 'TEXTAREA' && !el.readOnly) {
            return true;
        }

        if (el.isContentEditable) {
            return true;
        }

        return false;
    }

    /**
    * Add the `focus-visible` class to the given element if it was not added by
    * the author.
    * @param {Element} el
    */
    private addFocusVisibleClass(el: Element) {

        // if(el.closest(".custom-focus")){
        //     return;
        // }

        if (el.matches(".custom-focus *")) {
            return;
        }

        if (el.classList.contains('focus-visible')) {
            return;
        }
        el.classList.add('focus-visible');
        el.setAttribute('data-focus-visible-added', '');
        try {
            el.dispatchEvent(new Event("focusinvisible", {
                bubbles: true,
                cancelable: false
            }));
        } catch (error) {

        }
    }

    /**
   * Remove the `focus-visible` class from the given element if it was not
   * originally added by the author.
   * @param {Element} el
   */
    private removeFocusVisibleClass(el: Element) {
        if (!el.hasAttribute('data-focus-visible-added')) {
            return;
        }
        el.classList.remove('focus-visible');
        el.removeAttribute('data-focus-visible-added');
        try {
            el.dispatchEvent(new Event("focusoutvisible", {
                bubbles: true,
                cancelable: false
            }));
        } catch (error) {

        }
    }

    /**
   * If the most recent user interaction was via the keyboard;
   * and the key press did not include a meta, alt/option, or control key;
   * then the modality is keyboard. Otherwise, the modality is not keyboard.
   * Apply `focus-visible` to any current active element and keep track
   * of our keyboard modality state with `hadKeyboardEvent`.
   * @param {KeyboardEvent} e
   */
    private onKeyDown(e: KeyboardEvent) {
        if (e.metaKey || e.altKey || e.ctrlKey) {
            return;
        }

        if (this.isValidFocusTarget((this.scope as any).activeElement)) {
            this.addFocusVisibleClass((this.scope as any).activeElement);
        }

        this.hadKeyboardEvent = true;
    }

    /**
   * If at any point a user clicks with a pointing device, ensure that we change
   * the modality away from keyboard.
   * This avoids the situation where a user presses a key on an already focused
   * element, and then clicks on a different element, focusing it with a
   * pointing device, while we still think we're in keyboard modality.
   * @param {Event} e
   */
    private onPointerDown(e: Event) {

        /**
         * Ignore "emulated" clicks, e.g.: 
         * NVDA will trigger mousedown for buttons etc.
         */
        if (e.type !== "touchstart") {
            var element = e.target as HTMLElement;
            if (element.matches !== undefined
                && !element.matches(":hover")) {
                return;
            }
        }

        this.hadKeyboardEvent = false;
    }

    /**
   * On `focus`, add the `focus-visible` class to the target if:
   * - the target received focus as a result of keyboard navigation, or
   * - the event target is an element that will likely require interaction
   *   via the keyboard (e.g. a text box)
   * @param {Event} e
   */
    private onFocus(e: Event) {
        // Prevent IE from focusing the document or HTML element.
        if (!this.isValidFocusTarget(e.target)) {
            return;
        }

        if (this.hadKeyboardEvent || this.focusTriggersKeyboardModality(e.target)) {
            this.addFocusVisibleClass(e.target as Element);
        }
    }

    /**
   * On `blur`, remove the `focus-visible` class from the target.
   * @param {Event} e
   */
    private onBlur(e: Event) {
        if (!this.isValidFocusTarget(e.target)) {
            return;
        }

        if (
            (e.target as any).classList.contains('focus-visible') ||
            (e.target as any).hasAttribute('data-focus-visible-added')
        ) {
            // To detect a tab/window switch, we look for a blur event followed
            // rapidly by a visibility change.
            // If we don't see a visibility change within 100ms, it's probably a
            // regular focus change.
            this.hadFocusVisibleRecently = true;
            if(this.hadFocusVisibleRecentlyTimeout !== null){
                window.clearTimeout(this.hadFocusVisibleRecentlyTimeout);
                this.hadFocusVisibleRecentlyTimeout = null;
            }
            this.hadFocusVisibleRecentlyTimeout = window.setTimeout(() => {
                this.hadFocusVisibleRecently = false;
            }, 100);
            this.removeFocusVisibleClass(e.target as Element);
        }
    }

    /**
   * If the user changes tabs, keep track of whether or not the previously
   * focused element had .focus-visible.
   * @param {Event} e
   */
    private onVisibilityChange(e: Event) {
        if (document.visibilityState === 'hidden') {
            // If the tab becomes active again, the browser will handle calling focus
            // on the element (Safari actually calls it twice).
            // If this tab change caused a blur on an element with focus-visible,
            // re-apply the class when the user switches back to the tab.
            if (this.hadFocusVisibleRecently) {
                this.hadKeyboardEvent = true;
            }
            this.addInitialPointerMoveListeners();
        }
    }

    /**
   * Add a group of listeners to detect usage of any pointing devices.
   * These listeners will be added when the polyfill first loads, and anytime
   * the window is blurred, so that they are active when the window regains
   * focus.
   */
    private addInitialPointerMoveListeners() {
        document.addEventListener('mousemove', this._onInitialPointerMove);
        document.addEventListener('mousedown', this._onInitialPointerMove);
        document.addEventListener('mouseup', this._onInitialPointerMove);
        document.addEventListener('pointermove', this._onInitialPointerMove);
        document.addEventListener('pointerdown', this._onInitialPointerMove);
        document.addEventListener('pointerup', this._onInitialPointerMove);
        document.addEventListener('touchmove', this._onInitialPointerMove);
        document.addEventListener('touchstart', this._onInitialPointerMove);
        document.addEventListener('touchend', this._onInitialPointerMove);
    }

    private removeInitialPointerMoveListeners() {
        document.removeEventListener('mousemove', this._onInitialPointerMove);
        document.removeEventListener('mousedown', this._onInitialPointerMove);
        document.removeEventListener('mouseup', this._onInitialPointerMove);
        document.removeEventListener('pointermove', this._onInitialPointerMove);
        document.removeEventListener('pointerdown', this._onInitialPointerMove);
        document.removeEventListener('pointerup', this._onInitialPointerMove);
        document.removeEventListener('touchmove', this._onInitialPointerMove);
        document.removeEventListener('touchstart', this._onInitialPointerMove);
        document.removeEventListener('touchend', this._onInitialPointerMove);
    }

    /**
     * When the polfyill first loads, assume the user is in keyboard modality.
     * If any event is received from a pointing device (e.g. mouse, pointer,
     * touch), turn off keyboard modality.
     * This accounts for situations where focus enters the page from the URL bar.
     * @param {Event} e
     */
    private onInitialPointerMove(e: Event) {
        // Work around a Safari quirk that fires a mousemove on <html> whenever the
        // window blurs, even if you're tabbing out of the page. ¯\_(ツ)_/¯
        if ((e.target as any).nodeName && (e.target as any).nodeName.toLowerCase() === 'html') {
            return;
        }

        this.hadKeyboardEvent = false;
        this.removeInitialPointerMoveListeners();
    }
}